import React, {useState} from 'react'
import {Modal, Button, Form, Alert} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {GET_CONTACT_TAGS, ASSIGN_CONTACT_TAGS, UNASSIGN_CONTACT_TAGS} from '../../../gql/queries/tags'
import {IResGetContactTags, IResAssignContactTags, IResUnassignContactTags} from '../../../types/tags'
import {toast} from 'react-toastify'
import {useIntl, FormattedMessage} from 'react-intl'
import {RECIPIENT_MESSAGES as M} from '../recipientMessages'
import './BulkTagModal.css'

interface BulkTagModalProps {
  show: boolean
  onHide: () => void
  selectedContactIds: string[]
  onSuccess?: () => void
}

export const BulkTagModal: React.FC<BulkTagModalProps> = ({
  show,
  onHide,
  selectedContactIds,
  onSuccess,
}) => {
  const intl = useIntl()
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [action, setAction] = useState<'assign' | 'unassign'>('assign')

  const {data, loading} = useQuery<IResGetContactTags>(GET_CONTACT_TAGS, {
    skip: !show,
  })

  const [assignTags, {loading: assigning}] = useMutation<IResAssignContactTags>(ASSIGN_CONTACT_TAGS)
  const [unassignTags, {loading: unassigning}] = useMutation<IResUnassignContactTags>(UNASSIGN_CONTACT_TAGS)

  const tags = data?.contactTags || []

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    )
  }

  const handleSubmit = async () => {
    if (selectedTagIds.length === 0) {
      toast.error(intl.formatMessage(M.toastSelectTag))
      return
    }

    try {
      if (action === 'assign') {
        const result = await assignTags({
          variables: {
            contactIds: selectedContactIds,
            tagIds: selectedTagIds,
          },
        })
        toast.success(
          intl.formatMessage(M.toastTagsAssigned, {
            count: result.data?.assignContactTags.assignedCount || 0,
          })
        )
      } else {
        const result = await unassignTags({
          variables: {
            contactIds: selectedContactIds,
            tagIds: selectedTagIds,
          },
        })
        toast.success(
          intl.formatMessage(M.toastTagsRemoved, {
            count: result.data?.unassignContactTags.unassignedCount || 0,
          })
        )
      }
      
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorTags))
    }
  }

  const handleClose = () => {
    setSelectedTagIds([])
    setAction('assign')
    onHide()
  }

  return (
    <Modal show={show} onHide={handleClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage {...M.bulkTagTitle} /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='info' className='mb-4'>
          <i className='bi bi-info-circle me-2'></i>
          <FormattedMessage
            {...M.contactsSelected}
            values={{
              count: selectedContactIds.length,
              b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        </Alert>

        {/* Action Selection */}
        <div className='mb-4'>
          <h6><FormattedMessage {...M.action} /></h6>
          <div className='d-flex gap-3'>
            <Form.Check
              type='radio'
              id='action-assign'
              label={intl.formatMessage(M.bulkTagAssign)}
              checked={action === 'assign'}
              onChange={() => setAction('assign')}
            />
            <Form.Check
              type='radio'
              id='action-unassign'
              label={intl.formatMessage(M.bulkTagUnassign)}
              checked={action === 'unassign'}
              onChange={() => setAction('unassign')}
            />
          </div>
        </div>

        {/* Tag Selection */}
        <div className='tag-selection'>
          <h6 className='mb-3'><FormattedMessage {...M.bulkTagSelect} /></h6>
          {loading ? (
            <div className='text-center py-4'>
              <div className='spinner-border spinner-border-sm' />
            </div>
          ) : tags.length === 0 ? (
            <Alert variant='warning'>
              <FormattedMessage {...M.bulkTagNoTags} />
            </Alert>
          ) : (
            <div className='tags-grid'>
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className={`tag-item ${selectedTagIds.includes(tag.id) ? 'selected' : ''}`}
                  onClick={() => toggleTag(tag.id)}
                >
                  <Form.Check
                    type='checkbox'
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={() => toggleTag(tag.id)}
                    label={
                      <div className='d-flex align-items-center gap-2'>
                        <div
                          className='tag-color'
                          style={{backgroundColor: tag.color}}
                        />
                        <span>{tag.name}</span>
                      </div>
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          <FormattedMessage {...M.cancel} />
        </Button>
        <Button
          variant={action === 'assign' ? 'primary' : 'danger'}
          onClick={handleSubmit}
          disabled={assigning || unassigning || selectedTagIds.length === 0}
        >
          {assigning || unassigning ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              <FormattedMessage {...M.processing} />
            </>
          ) : (
            <>
              {intl.formatMessage(action === 'assign' ? M.bulkTagAdd : M.bulkTagRemove)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
